// BookingTutorial.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  SafeAreaView,
  Image,
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Alert,
  Modal,
  Linking,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { FontAwesome } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';

const getTutorialVisitDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 5);
  d.setHours(19, 0, 0, 0);
  return d;
};

const formatTutorialVisitDate = (date) => {
  try {
    return date.toLocaleString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return date.toString();
  }
};

const BookingTutorial = ({ navigation }) => {
  const { width, height } = useWindowDimensions();
  const isMobile = width < 820;
  const scrollViewRef = useRef(null);

  const tutorialVisitDate = useMemo(() => getTutorialVisitDate(), []);

  // ------------------------------
  // Hard-coded tutorial booking data
  // ------------------------------
  const [userData] = useState({
    Username: 'Your Venue',
    VenueCode: '2468',
  });

  const [promoterName] = useState('Maya Collins');
  const [platformUsername] = useState('mayaeatslondon');
  const [platformFollowers] = useState('42.8K');
  const [profileImage] = useState(
    'https://collabbay.com/DefaultProfileIcon.png'
  );

  const [listingLocation] = useState('Soho, London');
  const [accountAdmin] = useState('your venue account');

  const [latestUpdate, setLatestUpdate] = useState({
    Message: 'Hey! I would love to visit and create a Reel and Stories for you.',
    CreditAvailableStart: formatTutorialVisitDate(tutorialVisitDate),
    Status: 'Pending',
    AddedVisitors: '1',
    LastUpdateFrom: 'Maya Collins',
    CreatorFirstName: 'Maya',
    CreatorLastName: 'Collins',
    CreatorPhoneNumber: '07•• ••• ••••',
    IsProposal: '',
  });

  const creatorSocialProof = useMemo(
    () => ({
      badges: [
        { key: 'accepted_count' },
        { key: 'delivered_content' },
      ],
      deliveredContentCount: 20,
      acceptedCount: 20,
      totalCollabsCount: 26,
      acceptedRatio: 77,
      promoter: 'Maya Collins',
    }),
    []
  );

  const [chatMessages, setChatMessages] = useState([
    {
      user: 'Maya Collins',
      text: 'Hey! I would love to visit and create a Reel and Stories for you.',
    },
  ]);

  // ------------------------------
  // Local-only interaction state
  // ------------------------------
  const [isHowToCollabVisible, setIsHowToCollabVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('CodeSystem');
  const [isExpanded, setIsExpanded] = useState(false);

  const [selectedDate, setSelectedDate] = useState(
    tutorialVisitDate.toDateString()
  );
  const [selectedTime, setSelectedTime] = useState('7:00 PM');
  const [message, setMessage] = useState('');

  const [isChatModalVisible, setIsChatModalVisible] = useState(false);
  const [newChatMessage, setNewChatMessage] = useState('');
  const [sendSuccess, setSendSuccess] = useState(false);

  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [cancelMessage, setCancelMessage] = useState('');

  const [showAnalytics, setShowAnalytics] = useState(false);

  const [accepted, setAccepted] = useState(false);
  const [showAcceptFlash, setShowAcceptFlash] = useState(false);

  // ------------------------------
  // Tutorial state
  // 0 intro
  // 1 creator
  // 2 date/time
  // 3 messages
  // 4 move/cancel
  // 5 accept
  // 6 sidebar
  // 7 calendar/browse/content
  // 8 email test + finish
  // ------------------------------
  const [tutorialStep, setTutorialStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState(null);

  const [userEmail, setUserEmail] = useState('');
  const [emailLookupLoading, setEmailLookupLoading] = useState(false);
  const [emailLookupError, setEmailLookupError] = useState('');
  const [emailTestState, setEmailTestState] = useState('idle');
  const [emailOutcome, setEmailOutcome] = useState('');

  const stepTargetId = {
    1: 'tutorialCreator',
    2: 'tutorialDate',
    3: 'tutorialMessages',
    4: 'tutorialMove',
    5: 'tutorialAccept',
    6: 'tutorialSidebar',
    7: 'tutorialSidebarTools',
  };

  const tutorialCopy = {
    1: {
      title: 'Review the creator',
      body:
        'Start with follower count and social proof. You can open Instagram, then view the influencer profile for deeper analytics before deciding.',
    },
    2: {
      title: 'Check the proposed date & time',
      body:
        'Every request arrives with a proposed visit slot. Make sure the date, time and location work for your venue.',
    },
    3: {
      title: 'Message the creator',
      body:
        'Use the message button for any quick questions or details. Messages stay attached to the booking.',
    },
    4: {
      title: 'Need another time?',
      body:
        'If the slot does not suit you, expand Move or cancel booking and propose a different date or time.',
    },
    5: {
      title: 'Accept the booking',
      body:
        'When you are happy, click Approve Collab. This tutorial will simulate the acceptance — nothing will be saved.',
    },
    6: {
      title: 'Your sidebar',
      body:
        'This is your main control centre. Home gets you back to the dashboard, while the tools below help you manage ongoing creator activity.',
    },
    7: {
      title: 'Three useful shortcuts',
      body:
        'My Calendar: check all upcoming bookings. Browse Creators: do not want to wait? Handpick creators to invite. Content Bank: all uploaded raw media stays accessible here.',
    },
  };

  // ------------------------------
  // Keep users inside tutorial until complete
  // ------------------------------
  const blockedNavigation = () => {
    Alert.alert(
      'Finish the quick tutorial first',
      'You can use the rest of the dashboard as soon as the walkthrough is complete.'
    );
  };

  // ------------------------------
  // Spotlight measurement
  // ------------------------------
  useEffect(() => {
    const id = stepTargetId[tutorialStep];

    if (!id || tutorialStep === 0 || tutorialStep === 8) {
      setSpotlightRect(null);
      return;
    }

    let timeout1;
    let timeout2;

    const measure = () => {
      if (typeof document !== 'undefined') {
        const el = document.getElementById(id);

        if (el) {
          const shouldScroll =
            tutorialStep >= 1 &&
            tutorialStep <= 5 &&
            tutorialStep !== 3;

          if (shouldScroll && el.scrollIntoView) {
            el.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest',
            });
          }

          timeout2 = setTimeout(() => {
            const r = el.getBoundingClientRect();
            setSpotlightRect({
              x: Math.max(0, r.left),
              y: Math.max(0, r.top),
              width: Math.max(1, r.width),
              height: Math.max(1, r.height),
            });
          }, shouldScroll ? 420 : 80);

          return;
        }
      }

      setSpotlightRect(null);
    };

    timeout1 = setTimeout(measure, 120);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
    };
  }, [tutorialStep, isExpanded, width, height]);

  // Automatically expand the move/cancel area for the move step
  useEffect(() => {
    if (tutorialStep === 4) {
      setIsExpanded(true);
    }
  }, [tutorialStep]);

  // ------------------------------
  // Final email lookup
  // TestBrandEmailReceipt.php will be added next.
  // GET ?mode=lookup => { success, email }
  // POST { mode: "send" } => sends only to the session user's DB email
  // ------------------------------
  useEffect(() => {
    if (tutorialStep !== 8 || userEmail || emailLookupLoading) return;

    setEmailLookupLoading(true);
    setEmailLookupError('');

    fetch(
      'https://collabbayadmin.com/APIControllers/TestBrandEmailReceipt.php?mode=lookup',
      {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
      }
    )
      .then((r) => r.json())
      .then((d) => {
        if (d?.success && d?.email) {
          setUserEmail(d.email);
        } else {
          setEmailLookupError(
            d?.message || 'We could not load your account email.'
          );
        }
      })
      .catch(() => {
        setEmailLookupError('We could not load your account email.');
      })
      .finally(() => {
        setEmailLookupLoading(false);
      });
  }, [tutorialStep, userEmail, emailLookupLoading]);

  const sendTestEmail = () => {
    if (emailTestState === 'sending') return;

    setEmailTestState('sending');

    fetch(
      'https://collabbayadmin.com/APIControllers/TestBrandEmailReceipt.php',
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mode: 'send',
        }),
      }
    )
      .then((r) => r.json())
      .then((d) => {
        if (d?.success) {
          setEmailTestState('sent');
        } else {
          setEmailTestState('error');
        }
      })
      .catch(() => {
        setEmailTestState('error');
      });
  };

  // ------------------------------
  // Local-only booking interactions
  // ------------------------------
  const handleSendChatMessage = () => {
    const trimmed = newChatMessage.trim();
    if (!trimmed) return;

    setChatMessages((prev) => [
      ...prev,
      {
        user: userData.Username,
        text: trimmed,
      },
    ]);

    setNewChatMessage('');
    setSendSuccess(true);

    setTimeout(() => {
      setSendSuccess(false);
      setIsChatModalVisible(false);
    }, 800);
  };

  const handleSubmit = () => {
    const fullDateTime = `${selectedDate} ${selectedTime}`;

    setLatestUpdate((prev) => ({
      ...prev,
      CreditAvailableStart: fullDateTime,
      Message: message || prev.Message,
    }));

    setMessage('');
    setIsExpanded(false);

    Alert.alert(
      'Tutorial update',
      'Perfect — this is how you would propose a new booking time. Nothing was saved.'
    );
  };

  const handleCancelBooking = () => {
    if (!cancelMessage.trim()) {
      Alert.alert('Add a message', 'A cancellation message would be required.');
      return;
    }

    setIsCancelModalVisible(false);
    setCancelMessage('');

    Alert.alert(
      'Tutorial only',
      'This demonstrates the cancellation flow. The booking has not actually been cancelled.'
    );
  };

  const handleTutorialAccept = () => {
    if (accepted || showAcceptFlash) return;

    setShowAcceptFlash(true);

    setTimeout(() => {
      setAccepted(true);
      setLatestUpdate((prev) => ({
        ...prev,
        Status: 'Accepted',
      }));
    }, 350);

    setTimeout(() => {
      setShowAcceptFlash(false);
      setTutorialStep(6);
    }, 1250);
  };

  const getCollabStatus = () => {
    if (accepted || latestUpdate.Status === 'Accepted') return 'Upcoming';
    return 'Pending';
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Upcoming':
        return styles.upcomingStatus;
      case 'Pending':
      default:
        return styles.pendingStatus;
    }
  };

  const getAISummary = () => {
    if (accepted) {
      return `${promoterName} is booked in 5 days.`;
    }
    return `${promoterName} wants to visit in 5 days.`;
  };

  const renderCreatorSocialProof = () => (
    <View style={styles.creatorSocialProofWrap}>
      {creatorSocialProof.badges.map((badge, index) => {
        let text = '';

        if (badge.key === 'accepted_count') {
          text = 'Booked by 20 other venues on collabbay';
        }

        if (badge.key === 'delivered_content') {
          text = '20 content deliveries on collabbay';
        }

        return (
          <View key={`${badge.key}-${index}`} style={styles.creatorProofBadge}>
            <Ionicons
              name="checkmark-circle"
              size={19}
              color="#1D9BF0"
              style={styles.creatorProofIcon}
            />
            <Text style={styles.creatorProofText}>{text}</Text>
          </View>
        );
      })}
    </View>
  );

  const renderPromoterProfile = () => (
    <View style={styles.profileCard}>
      <Image
        style={styles.profileImage}
        source={{ uri: profileImage }}
      />

      <Text style={styles.profileUsername}>@{platformUsername}</Text>

      <Text style={styles.profileFollowers}>
        {platformFollowers} Followers
      </Text>

      {renderCreatorSocialProof()}

      <TouchableOpacity
        onPress={() => Linking.openURL('https://www.instagram.com/')}
        style={styles.profileLinkButton}
      >
        <Ionicons name="logo-instagram" size={20} color="black" />
        <Text style={styles.profileLinkText}>View on Instagram</Text>
      </TouchableOpacity>
    </View>
  );

  const renderDatePicker = () => {
    const dates = [];
    const today = new Date();

    for (let i = 0; i < 30; i++) {
      const date = new Date();
      date.setDate(today.getDate() + i);
      dates.push(date.toDateString());
    }

    return (
      <View style={styles.datePickerContainer}>
        <Text style={styles.datePickerLabel}>Move Booking Date</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {dates.map((date, index) => (
            <TouchableOpacity
              key={index}
              style={[
                styles.dateOption,
                {
                  backgroundColor:
                    selectedDate === date ? '#FDF05D' : 'white',
                },
              ]}
              onPress={() => setSelectedDate(date)}
            >
              <Text style={styles.dateOptionText}>{date}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    );
  };

  const renderTimePicker = () => (
    <View style={styles.timePickerContainer}>
      <Text style={styles.timePickerLabel}>Select New Time</Text>

      <ScrollView
        style={styles.timeScrollView}
        showsVerticalScrollIndicator={false}
      >
        {Array.from({ length: 40 }, (_, i) => {
          const hour = Math.floor((i + 14) / 2) % 24;
          const minute = i % 2 === 0 ? '00' : '30';
          const ampm = hour >= 12 ? 'PM' : 'AM';
          const adjustedHour = hour % 12 === 0 ? 12 : hour % 12;
          const timeLabel = `${adjustedHour}:${minute} ${ampm}`;

          return (
            <TouchableOpacity
              key={i}
              style={[
                styles.timeSlot,
                selectedTime === timeLabel && styles.selectedTimeSlot,
              ]}
              onPress={() => setSelectedTime(timeLabel)}
            >
              <Text>{timeLabel}</Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );

  const finishTutorial = () => {
    if (!emailOutcome) return;

    if (navigation?.navigate) {
      navigation.navigate('MyPanel');
      return;
    }

    if (typeof window !== 'undefined') {
      window.location.href = 'https://collabbayadmin.com/';
    }
  };

  const goNext = () => {
    if (tutorialStep === 4) {
      setIsExpanded(false);
    }

    setTutorialStep((prev) => Math.min(8, prev + 1));
  };

  const renderSpotlightOverlay = () => {
    if (tutorialStep === 0) {
      return (
        <View style={styles.tutorialOverlay}>
          <View style={styles.tutorialFullScreenDim} />

          <View style={styles.tutorialCenterCard}>
            <View style={styles.tutorialWelcomeIcon}>
              <Ionicons name="sparkles" size={26} color="#111827" />
            </View>

            <View style={styles.tutorialStepPill}>
              <Text style={styles.tutorialStepPillText}>GUIDED SETUP</Text>
            </View>

            <Text style={styles.tutorialTitle}>
              Your first booking walkthrough
            </Text>

            <Text style={styles.tutorialBody}>
              Congrats — you are ready to receive creator bookings. Requests
              arrive on your dashboard and open into a page just like this.
              We will show you the important controls before you get started.
            </Text>

            <TouchableOpacity
              style={styles.tutorialNextButton}
              onPress={() => setTutorialStep(1)}
            >
              <Text style={styles.tutorialNextText}>Click to start</Text>
            </TouchableOpacity>
          </View>
        </View>
      );
    }

    if (tutorialStep === 8) {
      return (
        <View style={styles.tutorialOverlay}>
          <View style={styles.tutorialFullScreenDim} />

          <View style={styles.tutorialCenterCard}>
            <View style={styles.tutorialEmailBadge}>
              <Text style={styles.tutorialEmailBadgeText}>Important final check</Text>
            </View>

            <Text style={styles.tutorialTitle}>All done — happy collabing.</Text>

            <Text style={styles.tutorialBody}>
              One last thing: confirm that booking emails can reach you.
            </Text>

            <View style={styles.tutorialEmailBox}>
              <Text style={styles.tutorialEmailLabel}>Your booking email</Text>

              {emailLookupLoading ? (
                <ActivityIndicator size="small" color="#111827" />
              ) : (
                <Text style={styles.tutorialEmailText}>
                  {userEmail || 'Loading your account email…'}
                </Text>
              )}
            </View>

            {!!emailLookupError && (
              <Text style={styles.tutorialErrorText}>{emailLookupError}</Text>
            )}

            <TouchableOpacity
              style={[
                styles.tutorialTestButton,
                (!userEmail || emailTestState === 'sending') && {
                  opacity: 0.5,
                },
              ]}
              disabled={!userEmail || emailTestState === 'sending'}
              onPress={sendTestEmail}
            >
              <Text style={styles.tutorialTestButtonText}>
                {emailTestState === 'sending'
                  ? 'Sending test…'
                  : 'Test my email works'}
              </Text>
            </TouchableOpacity>

            {emailTestState === 'sent' && (
              <Text style={styles.tutorialSuccessText}>
                Test sent. Check your inbox, then confirm below.
              </Text>
            )}

            {emailTestState === 'error' && (
              <Text style={styles.tutorialErrorText}>
                We could not send the test. Please try again.
              </Text>
            )}

            <View style={styles.tutorialChoiceRow}>
              <TouchableOpacity
                style={[
                  styles.tutorialChoice,
                  emailOutcome === 'worked' &&
                    styles.tutorialChoiceSelected,
                ]}
                onPress={() => setEmailOutcome('worked')}
              >
                <Text
                  style={[
                    styles.tutorialChoiceText,
                    emailOutcome === 'worked' &&
                      styles.tutorialChoiceTextSelected,
                  ]}
                >
                  Email worked
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.tutorialChoice,
                  emailOutcome === 'missing' &&
                    styles.tutorialChoiceSelected,
                ]}
                onPress={() => setEmailOutcome('missing')}
              >
                <Text
                  style={[
                    styles.tutorialChoiceText,
                    emailOutcome === 'missing' &&
                      styles.tutorialChoiceTextSelected,
                  ]}
                >
                  I didn't get anything
                </Text>
              </TouchableOpacity>
            </View>

            {emailOutcome === 'worked' && (
              <Text style={styles.tutorialSuccessText}>
                You’re good to go – just sit back and wait for requests to come in.
              </Text>
            )}

            {emailOutcome === 'missing' && (
              <View style={styles.tutorialHelpBox}>
                <Text style={styles.tutorialHelpText}>
                  Check your spam/junk folder and mark the email as “not spam”.
                  If it is still missing, message us and we can help.
                </Text>
              </View>
            )}

            <TouchableOpacity
              style={[
                styles.tutorialNextButton,
                !emailOutcome && { opacity: 0.45 },
              ]}
              disabled={!emailOutcome}
              onPress={finishTutorial}
            >
              <Text style={styles.tutorialNextText}>Continue to homepage</Text>
            </TouchableOpacity>
          </View>
        </View>
      );
    }

    const rect = spotlightRect;
    const pad = 8;

    if (!rect) {
      return (
        <View style={styles.tutorialOverlay}>
          <View style={styles.tutorialFullScreenDim} />
          <View style={styles.tutorialTooltip}>
            <View style={styles.tutorialStepPill}>
              <Text style={styles.tutorialStepPillText}>
                STEP {tutorialStep} OF 8
              </Text>
            </View>

            <Text style={styles.tutorialTitle}>
              {tutorialCopy[tutorialStep]?.title}
            </Text>

            <Text style={styles.tutorialBody}>
              {tutorialCopy[tutorialStep]?.body}
            </Text>

            {tutorialStep !== 5 && (
              <TouchableOpacity
                style={styles.tutorialNextButton}
                onPress={goNext}
              >
                <Text style={styles.tutorialNextText}>Next</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      );
    }

    const x = Math.max(0, rect.x - pad);
    const y = Math.max(0, rect.y - pad);
    const w = Math.min(width - x, rect.width + pad * 2);
    const h = Math.min(height - y, rect.height + pad * 2);

    return (
      <View style={styles.tutorialOverlay} pointerEvents="box-none">
        <View
          style={[
            styles.tutorialDim,
            { left: 0, top: 0, right: 0, height: y },
          ]}
        />
        <View
          style={[
            styles.tutorialDim,
            { left: 0, top: y, width: x, height: h },
          ]}
        />
        <View
          style={[
            styles.tutorialDim,
            {
              left: x + w,
              top: y,
              right: 0,
              height: h,
            },
          ]}
        />
        <View
          style={[
            styles.tutorialDim,
            { left: 0, top: y + h, right: 0, bottom: 0 },
          ]}
        />

        <View
          pointerEvents="none"
          style={[
            styles.tutorialOutline,
            {
              left: x,
              top: y,
              width: w,
              height: h,
            },
          ]}
        />

        <View style={styles.tutorialTooltip}>
          <View style={styles.tutorialStepPill}>
            <Text style={styles.tutorialStepPillText}>
              STEP {tutorialStep} OF 8
            </Text>
          </View>

          <Text style={styles.tutorialTitle}>
            {tutorialCopy[tutorialStep]?.title}
          </Text>

          <Text style={styles.tutorialBody}>
            {tutorialCopy[tutorialStep]?.body}
          </Text>

          {tutorialStep === 5 ? (
            <Text style={styles.tutorialProgress}>
              Click the highlighted Approve Collab button to continue.
            </Text>
          ) : (
            <TouchableOpacity
              style={styles.tutorialNextButton}
              onPress={goNext}
            >
              <Text style={styles.tutorialNextText}>Next</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.appShell}>
      {showAcceptFlash && (
        <View style={styles.tutorialAcceptFlash}>
          <View style={styles.tutorialAcceptCircle}>
            <Ionicons name="checkmark" size={54} color="#16a34a" />
          </View>

          <Text style={styles.tutorialAcceptTitle}>Booking accepted</Text>
          <Text style={styles.tutorialAcceptSub}>
            Nice — that is all it takes.
          </Text>
        </View>
      )}

      <View style={styles.shellRow}>
        {/* SIDE NAVIGATION */}
        <View
          nativeID="tutorialSidebar"
          style={[styles.sideNav, isMobile && styles.mobileSideNav]}
        >
          <LinearGradient
            colors={['#050509', '#050505', '#000000']}
            style={[
              styles.sideNavInner,
              isMobile && styles.mobileSideNavInner,
            ]}
          >
            <LinearGradient
              colors={['rgba(253,240,93,0.08)', 'rgba(0,0,0,0)']}
              style={styles.sideNavGlow}
              start={{ x: 0.1, y: 0 }}
              end={{ x: 1, y: 1 }}
            />

            <View style={styles.sideNavHeader}>
              <Image
                source={{
                  uri: 'https://collabbayadmin.com/CollabbayLogoDark.png',
                }}
                style={styles.sideNavLogoImage}
                resizeMode="contain"
              />

              {!isMobile && (
                <View>
                  <Text style={styles.logoWordmark}>
                    <Text style={{ fontWeight: '300' }}>collab</Text>
                    bay<Text style={styles.logoDot}>.</Text>
                  </Text>
                  <Text style={styles.logoDesktopTag}>DESKTOP</Text>
                </View>
              )}
            </View>

            <View
              nativeID="tutorialSidebarTools"
              style={styles.sideNavSection}
            >
              {!isMobile && (
                <LinearGradient
                  colors={['#111111', '#2b2b2b']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={{
                    paddingTop: 4,
                    paddingBottom: 8,
                    paddingHorizontal: 10,
                    borderRadius: 999,
                    alignSelf: 'flex-start',
                    maxWidth: '100%',
                    marginLeft: 1,
                  }}
                >
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    style={{
                      fontSize: 8,
                      color: '#ffffff',
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                      textAlign: 'left',
                    }}
                  >
                    YOUR VENUE
                  </Text>
                </LinearGradient>
              )}

              <TouchableOpacity
                style={styles.navItem}
                onPress={blockedNavigation}
              >
                <View style={styles.navItemRow}>
                  <View style={styles.navItemMainLabel}>
                    <Ionicons
                      name="home-outline"
                      size={20}
                      color="#f5f5f5"
                      style={styles.navIcon}
                    />
                    {!isMobile && <Text style={styles.navText}>Home</Text>}
                  </View>
                </View>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.navItem}
                onPress={blockedNavigation}
              >
                <View style={styles.navItemRow}>
                  <View style={styles.navItemMainLabel}>
                    <Ionicons
                      name="pricetags-outline"
                      size={20}
                      color="#f5f5f5"
                      style={styles.navIcon}
                    />
                    {!isMobile && (
                      <Text style={styles.navText}>My Offers</Text>
                    )}
                  </View>
                </View>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.navItem}
                onPress={blockedNavigation}
              >
                <View style={styles.navItemRow}>
                  <View style={styles.navItemMainLabel}>
                    <Ionicons
                      name="calendar-outline"
                      size={20}
                      color="#f5f5f5"
                      style={styles.navIcon}
                    />
                    {!isMobile && (
                      <Text style={styles.navText}>My Calendar</Text>
                    )}
                  </View>
                </View>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.navItem}
                onPress={blockedNavigation}
              >
                <View style={styles.navItemRow}>
                  <View style={styles.navItemMainLabel}>
                    <Ionicons
                      name="people-outline"
                      size={20}
                      color="#f5f5f5"
                      style={styles.navIcon}
                    />
                    {!isMobile && (
                      <Text style={styles.navText}>Browse Creators</Text>
                    )}
                  </View>
                </View>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.navItem}
                onPress={blockedNavigation}
              >
                <View style={styles.navItemRow}>
                  <View style={styles.navItemMainLabel}>
                    <Ionicons
                      name="images-outline"
                      size={20}
                      color="#f5f5f5"
                      style={styles.navIcon}
                    />
                    {!isMobile && (
                      <Text style={styles.navText}>Content Bank</Text>
                    )}
                  </View>
                </View>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.navItem}
                onPress={blockedNavigation}
              >
                <View style={styles.navItemRow}>
                  <View style={styles.navItemMainLabel}>
                    <Ionicons
                      name="chatbubbles-outline"
                      size={20}
                      color="#f5f5f5"
                      style={styles.navIcon}
                    />
                    {!isMobile && (
                      <Text style={styles.navText}>Activity</Text>
                    )}
                  </View>
                </View>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.navItem}
                onPress={blockedNavigation}
              >
                <View style={styles.navItemRow}>
                  <View style={styles.navItemMainLabel}>
                    <Ionicons
                      name="person-circle-outline"
                      size={20}
                      color="#f5f5f5"
                      style={styles.navIcon}
                    />
                    {!isMobile && (
                      <Text style={styles.navText}>Account</Text>
                    )}
                  </View>
                </View>
              </TouchableOpacity>
            </View>

            {!isMobile && (
              <View style={styles.sideNavSection}>
                <TouchableOpacity
                  style={styles.peerMapCard}
                  activeOpacity={0.9}
                  onPress={blockedNavigation}
                >
                  <LinearGradient
                    colors={[
                      'rgba(255,255,255,0.08)',
                      'rgba(0,0,0,0.4)',
                    ]}
                    style={styles.peerMapInner}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                  >
                    <View style={styles.peerMapContent}>
                      <Ionicons
                        name="map-outline"
                        size={18}
                        color="#FDF05D"
                        style={{ marginRight: 8 }}
                      />
                      <View style={{ flex: 1 }}>
                        <Text style={styles.peerMapTitle}>Peer Map</Text>
                        <Text style={styles.peerMapSubtitle}>
                          View 100+ venues on Collabbay
                        </Text>
                      </View>
                    </View>
                  </LinearGradient>
                </TouchableOpacity>
              </View>
            )}

            {!isMobile && (
              <View style={styles.sideNavBottom}>
                <TouchableOpacity
                  style={styles.bottomLink}
                  onPress={blockedNavigation}
                >
                  <Ionicons
                    name="help-circle-outline"
                    size={18}
                    color="#f5f5f5"
                    style={{ marginRight: 8 }}
                  />
                  <View>
                    <Text style={styles.bottomLinkText}>FAQs</Text>
                    <Text style={styles.bottomLinkCaption}>
                      Common questions answered
                    </Text>
                  </View>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.bottomLink}
                  onPress={blockedNavigation}
                >
                  <Ionicons
                    name="headset-outline"
                    size={18}
                    color="#f5f5f5"
                    style={{ marginRight: 8 }}
                  />
                  <View>
                    <Text style={styles.bottomLinkText}>Help</Text>
                    <Text style={styles.bottomLinkCaption}>
                      We&apos;re here 24/7
                    </Text>
                  </View>
                </TouchableOpacity>
              </View>
            )}
          </LinearGradient>
        </View>

        {/* MAIN AREA */}
        <View style={styles.mainArea}>
          <ScrollView
            ref={scrollViewRef}
            contentContainerStyle={[
              styles.contentContainer,
              isMobile && styles.mobileContentContainer,
            ]}
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.pageInner}>
              <View style={styles.pageHeaderRow}>
                <TouchableOpacity
                  style={styles.backButton}
                  onPress={blockedNavigation}
                >
                  <Ionicons name="arrow-back" size={20} color="black" />
                  {!isMobile && (
                    <Text style={styles.backButtonText}>
                      Back to activity
                    </Text>
                  )}
                </TouchableOpacity>

                <Text style={styles.tutorialBadge}>Tutorial</Text>

                <View style={styles.statusRowTop}>
                  <View
                    style={[
                      styles.statusBadgeTop,
                      getStatusStyle(getCollabStatus()),
                    ]}
                  >
                    <Text style={styles.statusText}>
                      {getCollabStatus()}
                    </Text>

                    {!isMobile && (
                      <View style={styles.statusAiRow}>
                        <Text style={styles.statusAiSparkle}>✨</Text>
                        <Text
                          style={styles.statusAiText}
                          numberOfLines={1}
                        >
                          {getAISummary()}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
              </View>

              {/* HOW TO COLLAB CARD — same structure as live page */}
              <View style={styles.howToCard}>
                <TouchableOpacity
                  onPress={() =>
                    setIsHowToCollabVisible((prev) => !prev)
                  }
                  activeOpacity={0.8}
                >
                  <View style={styles.howToHeaderRow}>
                    <Text style={styles.howToTitle}>
                      How to collab <Text style={styles.howToDot}>•</Text>
                    </Text>
                    <Ionicons
                      name={
                        isHowToCollabVisible
                          ? 'chevron-up'
                          : 'chevron-down'
                      }
                      size={24}
                      color="black"
                    />
                  </View>
                </TouchableOpacity>

                {isHowToCollabVisible && (
                  <>
                    <View style={styles.tabSwitcherRow}>
                      <TouchableOpacity
                        style={[
                          styles.tabSwitch,
                          activeTab === 'CodeSystem' &&
                            styles.tabSwitchActive,
                        ]}
                        onPress={() => setActiveTab('CodeSystem')}
                      >
                        <Text
                          style={[
                            styles.tabSwitchText,
                            activeTab === 'CodeSystem' &&
                              styles.tabSwitchTextActive,
                          ]}
                        >
                          Using a code
                        </Text>
                      </TouchableOpacity>

                      <TouchableOpacity
                        style={[
                          styles.tabSwitch,
                          activeTab === 'QRSystem' &&
                            styles.tabSwitchActive,
                        ]}
                        onPress={() => setActiveTab('QRSystem')}
                      >
                        <Text
                          style={[
                            styles.tabSwitchText,
                            activeTab === 'QRSystem' &&
                              styles.tabSwitchTextActive,
                          ]}
                        >
                          Using QRs
                        </Text>
                      </TouchableOpacity>
                    </View>

                    <View style={styles.howToBody}>
                      <Text style={styles.howToStepText}>
                        <Text style={styles.howToStepNumber}>1-</Text>{' '}
                        The influencer arrives, mentioning they are with
                        collabbay.
                      </Text>
                      <Text style={styles.howToStepText}>
                        <Text style={styles.howToStepNumber}>2-</Text>{' '}
                        Provide the agreed offer and verify their visit.
                      </Text>
                      <Text style={styles.howToStepText}>
                        <Text style={styles.howToStepNumber}>3-</Text>{' '}
                        The creator captures their content on-site.
                      </Text>
                      <Text style={styles.howToStepText}>
                        <Text style={styles.howToStepNumber}>4-</Text>{' '}
                        Content is delivered after the visit.
                      </Text>
                    </View>
                  </>
                )}
              </View>

              {/* PROFILE + APPROVE */}
              <View
                style={[
                  styles.topRow,
                  isMobile && styles.mobileTopRow,
                ]}
              >
                <View
                  style={[
                    styles.profileColumn,
                    isMobile && styles.mobileProfileColumn,
                  ]}
                >
                  <View
                    nativeID="tutorialCreator"
                    style={{ position: 'relative', width: '100%' }}
                  >
                    {renderPromoterProfile()}

                    <TouchableOpacity
                      style={styles.influencerProfileButton}
                      onPress={() => setShowAnalytics(true)}
                    >
                      <Text style={styles.influencerProfileButtonText}>
                        View Influencer Profile
                      </Text>
                      <Ionicons
                        name="arrow-forward"
                        size={20}
                        color="black"
                      />
                    </TouchableOpacity>
                  </View>

                  <View
                    nativeID="tutorialMessages"
                    style={styles.contactCreatorCard}
                  >
                    <TouchableOpacity
                      style={styles.contactCreatorButton}
                      onPress={() => setIsChatModalVisible(true)}
                    >
                      <Ionicons
                        name="mail-outline"
                        size={24}
                        color="black"
                        style={styles.contactIcon}
                      />
                      <Text style={styles.contactButtonText}>
                        Contact Creator
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={styles.copyEmailButton}
                      onPress={() => setIsChatModalVisible(true)}
                    >
                      <Ionicons
                        name="chatbox-ellipses-outline"
                        size={20}
                        color="black"
                        style={styles.copyEmailIcon}
                      />
                    </TouchableOpacity>
                  </View>
                </View>

                <View
                  style={[
                    styles.statusColumn,
                    isMobile && styles.mobileStatusColumn,
                  ]}
                >
                  <View nativeID="tutorialAccept">
                    {!accepted ? (
                      <View style={styles.approveButtonContainer}>
                        <TouchableOpacity
                          style={styles.approveButton}
                          onPress={handleTutorialAccept}
                          activeOpacity={0.85}
                        >
                          <View style={{ alignItems: 'center' }}>
                            <Text style={styles.approveButtonLabel}>
                              <Ionicons
                                name="checkmark-circle"
                                size={20}
                                color="black"
                              />{' '}
                              Approve Collab
                            </Text>

                            {!!accountAdmin && (
                              <View style={{ marginTop: 8 }}>
                                <Text style={styles.accountAdminText}>
                                  Tutorial simulation
                                </Text>
                              </View>
                            )}
                          </View>
                        </TouchableOpacity>
                      </View>
                    ) : (
                      <View
                        style={[
                          styles.approveButton,
                          { backgroundColor: '#dcfce7' },
                        ]}
                      >
                        <Text
                          style={[
                            styles.approveButtonLabel,
                            { color: '#166534' },
                          ]}
                        >
                          ✓ Booking Accepted
                        </Text>
                      </View>
                    )}
                  </View>

                  <View nativeID="tutorialDate" style={styles.statusCard}>
                    <View style={styles.statusTitleContainer}>
                      <Text style={styles.subheader}>Collab Status</Text>
                    </View>

                    <Text style={styles.statusLabel}>
                      Proposed Booking Date:{' '}
                      {latestUpdate.CreditAvailableStart}
                    </Text>

                    <View style={styles.locationChip}>
                      <Text style={styles.locationText}>
                        📍 {listingLocation}
                      </Text>
                    </View>

                    <Text style={styles.statusLabel}>
                      Status:{' '}
                      {accepted
                        ? 'Booking confirmed.'
                        : 'Booking not yet confirmed. Pending approval.'}
                    </Text>

                    <View style={styles.aiInlineRow}>
                      <Text style={styles.aiInlineSparkle}>✨</Text>
                      <Text style={styles.aiInlineText}>
                        {getAISummary()}
                      </Text>
                    </View>

                    <Text style={styles.statusLabel}>
                      Added Visitors (Plus-ones):{' '}
                      {latestUpdate.AddedVisitors || 'None'}
                    </Text>

                    <Text style={styles.subheader}>Creator Details</Text>

                    <Text style={styles.statusLabel}>
                      First Name: {latestUpdate.CreatorFirstName}
                    </Text>

                    <Text style={styles.statusLabel}>
                      Last Name: {latestUpdate.CreatorLastName}
                    </Text>

                    <Text style={styles.statusLabel}>
                      Mobile: {latestUpdate.CreatorPhoneNumber}
                    </Text>

                    <View style={styles.chatContainer}>
                      <View style={styles.chatHeader}>
                        <Ionicons
                          name="chatbox-outline"
                          size={20}
                          color="black"
                        />
                        <Text style={styles.chatHeaderText}>Messages</Text>
                      </View>

                      {chatMessages.map((msg, index) => (
                        <View
                          key={index}
                          style={[
                            styles.chatBubble,
                            msg.user === userData.Username
                              ? styles.sentMessage
                              : styles.receivedMessage,
                          ]}
                        >
                          <Text style={styles.chatUser}>{msg.user}</Text>
                          <Text style={styles.chatText}>{msg.text}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                </View>
              </View>

              {/* MOVE / CANCEL */}
              <TouchableOpacity
                nativeID="tutorialMove"
                style={styles.header}
                onPress={() => setIsExpanded((prev) => !prev)}
              >
                <Text style={styles.updateToggleText}>
                  Move or cancel booking?{' '}
                  <Ionicons
                    name={isExpanded ? 'chevron-up' : 'chevron-down'}
                    size={20}
                    color="black"
                  />
                </Text>
              </TouchableOpacity>

              {isExpanded && (
                <View
                  style={[styles.updateCollabCard, styles.centeredContainer]}
                >
                  {renderDatePicker()}
                  {renderTimePicker()}

                  <TextInput
                    style={styles.textInput}
                    placeholder="Add a message..."
                    placeholderTextColor="gray"
                    value={message}
                    onChangeText={setMessage}
                    multiline
                    maxLength={400}
                  />

                  <Text style={styles.charCounter}>
                    {message.length} / 400
                  </Text>

                  <TouchableOpacity
                    style={styles.button}
                    onPress={handleSubmit}
                  >
                    <Text style={styles.buttonText}>Propose Update</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[styles.button, { backgroundColor: 'white' }]}
                    onPress={() => setIsCancelModalVisible(true)}
                  >
                    <Ionicons
                      name="warning-outline"
                      size={20}
                      color="red"
                    />
                    <Text style={styles.cancelCtaText}>
                      Need to Cancel?
                    </Text>
                  </TouchableOpacity>

                  {isCancelModalVisible && (
                    <View style={styles.cancelModal}>
                      <View style={styles.cancelModalContent}>
                        <TouchableOpacity
                          style={styles.closeButton}
                          onPress={() => setIsCancelModalVisible(false)}
                        >
                          <Ionicons
                            name="close-circle-outline"
                            size={24}
                            color="black"
                          />
                        </TouchableOpacity>

                        <Text style={styles.cancelModalTitle}>
                          Are you sure you want to cancel?
                        </Text>

                        <Text style={styles.cancelModalSubtitle}>
                          Add a message (required):
                        </Text>

                        <TextInput
                          style={styles.textInput}
                          placeholder="Enter a message..."
                          placeholderTextColor="gray"
                          value={cancelMessage}
                          onChangeText={setCancelMessage}
                          multiline
                          maxLength={400}
                        />

                        <View style={styles.cancelModalButtons}>
                          <TouchableOpacity
                            style={{ padding: 10 }}
                            onPress={handleCancelBooking}
                          >
                            <Text style={styles.cancelModalConfirmText}>
                              Yes, Cancel Booking
                            </Text>
                          </TouchableOpacity>

                          <TouchableOpacity
                            style={styles.cancelModalBackButton}
                            onPress={() => setIsCancelModalVisible(false)}
                          >
                            <Text style={styles.cancelModalBackText}>
                              No, Go Back
                            </Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    </View>
                  )}
                </View>
              )}
            </View>
          </ScrollView>
        </View>
      </View>

      {/* DUMMY CREATOR ANALYTICS */}
      <Modal
        animationType="fade"
        transparent
        visible={showAnalytics}
        onRequestClose={() => setShowAnalytics(false)}
      >
        <View style={styles.analyticsOverlay}>
          <View style={styles.analyticsCard}>
            <Text style={styles.analyticsTitle}>
              Creator analytics
            </Text>

            <View style={styles.analyticsMetricRow}>
              <Text style={styles.analyticsMetricLabel}>
                Average Reel views
              </Text>
              <Text style={styles.analyticsMetricValue}>18.4K</Text>
            </View>

            <View style={styles.analyticsMetricRow}>
              <Text style={styles.analyticsMetricLabel}>
                Engagement rate
              </Text>
              <Text style={styles.analyticsMetricValue}>5.8%</Text>
            </View>

            <View style={styles.analyticsMetricRow}>
              <Text style={styles.analyticsMetricLabel}>UK audience</Text>
              <Text style={styles.analyticsMetricValue}>78%</Text>
            </View>

            <View style={styles.analyticsMetricRow}>
              <Text style={styles.analyticsMetricLabel}>
                Collabbay content delivered
              </Text>
              <Text style={styles.analyticsMetricValue}>20</Text>
            </View>

            <TouchableOpacity
              style={styles.tutorialNextButton}
              onPress={() => setShowAnalytics(false)}
            >
              <Text style={styles.tutorialNextText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* LOCAL-ONLY CHAT */}
      <Modal
        animationType="slide"
        transparent
        visible={isChatModalVisible}
        onRequestClose={() => setIsChatModalVisible(false)}
      >
        <View style={styles.chatModalOverlay}>
          <View style={styles.chatModalContainer}>
            <View style={styles.chatHeaderSection}>
              <Ionicons name="chatbox-outline" size={24} color="black" />
              <Text style={styles.chatHeaderTitle}>
                Chat with {promoterName}
              </Text>

              <TouchableOpacity
                onPress={() => setIsChatModalVisible(false)}
                style={styles.chatCloseButton}
              >
                <Ionicons name="close" size={24} color="black" />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.chatMessagesSection}>
              {chatMessages.map((msg, index) => (
                <View
                  key={index}
                  style={[
                    styles.chatBubble,
                    msg.user === userData.Username
                      ? styles.sentMessage
                      : styles.receivedMessage,
                  ]}
                >
                  <Text style={styles.chatUser}>{msg.user}</Text>
                  <Text style={styles.chatText}>{msg.text}</Text>
                </View>
              ))}
            </ScrollView>

            {sendSuccess && (
              <View style={{ alignItems: 'center', marginVertical: 10 }}>
                <Ionicons
                  name="checkmark-circle"
                  size={32}
                  color="green"
                />
                <Text style={{ color: 'green', marginTop: 5 }}>
                  Message sent
                </Text>
              </View>
            )}

            <View style={styles.chatInputSection}>
              <TextInput
                placeholder="Type your message..."
                placeholderTextColor="#888"
                style={styles.chatTextInput}
                value={newChatMessage}
                onChangeText={setNewChatMessage}
                multiline
                maxLength={400}
              />

              <Text style={styles.charCounter}>
                {newChatMessage.length} / 400
              </Text>

              <TouchableOpacity
                style={styles.sendButton}
                onPress={handleSendChatMessage}
              >
                <Ionicons name="send" size={24} color="black" />
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {renderSpotlightOverlay()}
    </SafeAreaView>
  );
};


const styles = StyleSheet.create({
  // APP SHELL
  appShell: {
    flex: 1,
    backgroundColor: '#f3f3f3',
  },
  loadingCenter: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    color: '#555',
    fontSize: 14,
  },
  shellRow: {
    flex: 1,
    flexDirection: 'row',
  },

  // SIDE NAV
  sideNav: {
    width: 250,
    backgroundColor: '#000',
  },
  sideNavInner: {
    flex: 1,
    paddingVertical: 24,
    paddingHorizontal: 16,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
    overflow: 'hidden',
    position: 'relative',
  },

  sideNavGlow: {
  position: 'absolute',
  top: 140,
  left: -60,
  width: 260,
  height: 260,
  borderRadius: 130,
  opacity: 0.55, // still subtle because the color alpha is 0.08
},

  sideNavHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 26,
    zIndex: 2,
  },
  sideNavLogoImage: {
    width: 38,
    height: 38,
    marginRight: 10,
  },
  logoWordmark: {
    fontSize: 16,
    color: '#f5f5f5',
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  logoDot: {
    color: '#FDF05D',
    fontSize: 18,
    fontWeight: '800',
  },
  logoDesktopTag: {
    fontSize: 9,
    color: '#b3b3b3',
    letterSpacing: 2,
    marginTop: 2,
  },
  sideNavSection: {
    marginTop: 12,
    marginBottom: 20,
    zIndex: 2,
  },
  sideNavSectionLabel: {
    fontSize: 11,
    textTransform: 'uppercase',
    color: '#8c8c8c',
    marginBottom: 8,
    letterSpacing: 1,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 8,
    borderRadius: 10,
    marginBottom: 6,
  },
  navIcon: {
    marginRight: 10,
  },
  navItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
  },
  navItemMainLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  navText: {
    color: '#f5f5f5',
    fontSize: 14,
    fontWeight: '500',
  },
  lockIcon: {
    marginLeft: 6,
  },

  // Peer Map card
  peerMapCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 4,
    marginBottom: 10,
  },
  peerMapInner: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  peerMapContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  peerMapTitle: {
    color: '#f5f5f5',
    fontSize: 13,
    fontWeight: '600',
  },
  peerMapSubtitle: {
    color: '#b3b3b3',
    fontSize: 11,
    marginTop: 2,
  },

  // Subscribe CTA
  subscribeCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 10,
  },
  subscribeGradient: {
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  subscribeContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  subscribeTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#000',
  },
  subscribeSubtitle: {
    fontSize: 11,
    color: '#1f2933',
    marginTop: 2,
  },

  // BOTTOM LINKS
  sideNavBottom: {
    marginTop: 'auto',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#181818',
    zIndex: 2,
  },
  bottomLink: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
  },
  bottomLinkText: {
    fontSize: 13,
    color: '#f5f5f5',
    fontWeight: '600',
  },
  bottomLinkCaption: {
    fontSize: 11,
    color: '#b3b3b3',
    marginTop: 2,
  },

  // MAIN AREA
  mainArea: {
    flex: 1,
    backgroundColor: '#f3f3f3',
  },
  contentContainer: {
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  pageInner: {
    width: '100%',
    maxWidth: 1100,
    alignSelf: 'center',
  },

  // HEADER + STATUS
  pageHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 999,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
  },
  backButtonText: {
    marginLeft: 6,
    fontSize: 13,
    color: '#333',
    fontWeight: '500',
  },

  statusRowTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  statusBadgeTop: {
    borderRadius: 7,
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: '#e3e3e3',
    maxWidth: 420,
  },
  statusText: {
    color: 'black',
    fontWeight: 'bold',
    padding: 7,
    fontSize: 14,
  },
  statusAiRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 2,
  },
  statusAiSparkle: {
    fontSize: 13,
    marginRight: 4,
  },
  statusAiText: {
    fontSize: 11,
    color: '#333',
    flex: 1,
  },

  // HOW TO CARD
  howToCard: {
    backgroundColor: '#f2f2ee',
    borderRadius: 16,
    padding: 16,
    shadowOpacity: 0.1,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 5,
    elevation: 3,
    marginBottom: 18,
  },
  howToHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  howToTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'black',
  },
  howToDot: {
    fontWeight: 'bold',
    color: '#FDF05D',
  },
  tabSwitcherRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 12,
    marginBottom: 12,
  },
  tabSwitch: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderColor: '#ccc',
  },
  tabSwitchActive: {
    borderColor: 'black',
  },
  tabSwitchText: {
    color: '#777',
    fontWeight: '600',
    fontSize: 13,
  },
  tabSwitchTextActive: {
    color: 'black',
  },
  howToBody: {
    marginTop: 8,
  },
  howToStepText: {
    fontSize: 14,
    color: '#555',
    marginVertical: 5,
    lineHeight: 22,
  },
  howToStepNumber: {
    fontWeight: 'bold',
    color: '#000',
  },
  howToHighlight: {
    fontWeight: 'bold',
    color: '#000',
  },

  // TOP ROW: PROFILE + STATUS
  topRow: {
    flexDirection: 'row',
    marginTop: 12,
    marginBottom: 20,
  },
  profileColumn: {
    width: '34%',
    marginRight: 14,
  },
  statusColumn: {
    flex: 1,
    marginLeft: 6,
  },

  // PROFILE CARD
  profileCard: {
    backgroundColor: '#F1F1F1',
    padding: 16,
    borderRadius: 18,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.08,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 4,
    elevation: 3,
    marginBottom: 16,
  },
  profileImage: {
    width: 180,
    height: 180,
    borderRadius: 60,
    marginBottom: 10,
  },
  profileUsername: {
    fontSize: 26,
    fontWeight: 'bold',
    color: 'black',
    marginBottom: 4,
  },
  profileFollowers: {
    fontSize: 23,
    color: 'gray',
    marginBottom: 8,
  },
  profileLinkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fdf05d',
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 999,
    marginTop: 4,
  },
  profileLinkText: {
    marginLeft: 6,
    fontSize: 13,
    color: '#000',
    fontWeight: '500',
  },

  influencerProfileButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#D0D0D0',
    borderRadius: 999,
    paddingVertical: 10,
    marginVertical: 6,
    paddingHorizontal: 18,
    alignSelf: 'flex-start',
  },
  influencerProfileButtonText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: 'black',
    marginRight: 8,
  },

  // CONTACT / SHARE / CHAT
  contactCreatorCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 8,
    marginTop: 8,
    borderRadius: 12,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#eee',
  },
  contactCreatorButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 12,
    flex: 1,
    borderWidth: 1,
    borderColor: '#f3f3f3',
  },
  copyEmailButton: {
    backgroundColor: '#f3f3f3',
    borderRadius: 12,
    padding: 8,
    marginLeft: 6,
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#f3f3f3',
  },
  contactIcon: {
    marginRight: 8,
  },
  contactButtonText: {
    fontSize: 13,
    fontWeight: 'bold',
    color: 'black',
  },
  copyEmailIcon: {
    alignSelf: 'center',
  },
  copiedText: {
    marginTop: 6,
    fontSize: 12,
    color: '#fff',
    fontWeight: '600',
    textAlign: 'center',
    backgroundColor: '#28a745',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },

  toastCard: {
    position: 'absolute',
    top: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 10,
    backgroundColor: '#28a745',
    borderRadius: 8,
    zIndex: 999,
    elevation: 3,
  },
  toastText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
  },

  // STATUS CARD
  statusCard: {
    backgroundColor: '#f3f3f3',
    padding: 16,
    marginVertical: 6,
    elevation: 2,
    shadowColor: 'black',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    borderRadius: 20,
  },
  subheader: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 4,
    color: 'black',
  },
  statusTitleContainer: {
    marginBottom: 6,
  },
  pendingWithChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fdf05d',
    borderRadius: 19,
    paddingVertical: 6,
    paddingHorizontal: 12,
    marginTop: 8,
    alignSelf: 'flex-start',
  },
  pendingWithText: {
    fontSize: 14,
    color: 'black',
    fontWeight: '500',
  },

  onBehalfOfChip: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    marginTop: 6,
    marginBottom: 7,
    backgroundColor: '#fff',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 15,
    borderWidth: 1,
    borderColor: '#ccc',
  },
  onBehalfOfLabel: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#333',
    marginRight: 5,
  },
  onBehalfOfValue: {
    fontSize: 16,
    color: '#333',
    fontWeight: '600',
  },

  statusLabel: {
    fontSize: 14,
    marginBottom: 8,
    backgroundColor: 'white',
    padding: 10,
    borderRadius: 16,
    color: 'black',
  },

  locationChip: {
    marginTop: 4,
    alignSelf: 'flex-start',
    backgroundColor: '#f3f3f3',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 15,
    marginLeft: 4,
    borderWidth: 1,
    marginBottom: 12,
    borderColor: '#bcbcbc',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 2,
  },
  locationText: {
    fontSize: 15,
    color: '#444',
  },

  // ✨ AI INLINE
  aiInlineRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#ffffff',
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginBottom: 8,
  },
  aiInlineSparkle: {
    fontSize: 16,
    marginRight: 6,
  },
  aiInlineText: {
    flex: 1,
    fontSize: 13,
    color: '#333',
  },

  // APPROVE BUTTON
  approveButton: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#74dc8a',
    borderRadius: 999,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginBottom: 7,
    minWidth: 200,
    flexDirection: 'row',
  },
  approveButtonContainer: {
    alignItems: 'flex-end',
    marginBottom: 10,
  },
  approveButtonInnerText: {
    fontWeight: 'bold',
    color: 'black',
    fontSize: 12,
    textAlign: 'center',
  },
  approveButtonLabel: {
    fontWeight: 'bold',
    textAlign: 'center',
    color: 'black',
    fontSize: 14,
  },
  accountAdminText: {
    fontSize: 9,
    color: 'black',
    textAlign: 'center',
  },

  // STATUS STYLES
  expiredStatus: {
    backgroundColor: 'grey',
  },
  redeemedStatus: {
    backgroundColor: 'green',
  },
  upcomingStatus: {
    backgroundColor: 'lightgreen',
  },
  pendingStatus: {
    backgroundColor: 'yellow',
  },
  cancelledStatus: {
    backgroundColor: 'red',
  },

  // DATE / TIME PICKERS
  datePickerContainer: {
    marginBottom: 18,
  },
  datePickerLabel: {
    fontSize: 14,
    marginBottom: 6,
    color: 'black',
    fontWeight: 'bold',
  },
  dateOption: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    marginRight: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'black',
  },
  dateOptionText: {
    fontSize: 13,
    color: 'black',
  },
  timePickerContainer: {
    marginBottom: 10,
    width: '100%',
  },
  timePickerLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
    color: 'black',
  },
  timeScrollView: {
    height: 120,
  },
  timeSlot: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderColor: '#eee',
  },
  selectedTimeSlot: {
    backgroundColor: '#FDF05D',
  },

  // UPDATE / CANCEL CARD
  header: {
    marginTop: 4,
  },
  updateToggleText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: 'black',
    marginVertical: 8,
    textAlign: 'center',
    backgroundColor: '#e9e9e9',
    borderRadius: 10,
    paddingVertical: 8,
  },
  updateCollabCard: {
    backgroundColor: '#fff',
    borderRadius: 15,
    padding: 16,
    marginVertical: 12,
    width: '100%',
  },
  centeredContainer: {
    alignSelf: 'center',
  },

  textInput: {
    borderWidth: 1,
    borderColor: 'black',
    borderRadius: 8,
    padding: 8,
    marginTop: 6,
    marginBottom: 8,
    fontSize: 14,
    color: 'black',
    minHeight: 60,
  },
  button: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'black',
    borderRadius: 999,
    paddingVertical: 10,
    marginTop: 4,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: 'white',
  },
  cancelCtaText: {
    color: 'red',
    textAlign: 'center',
    marginLeft: 6,
    fontWeight: '600',
  },

  charCounter: {
    marginTop: 4,
    marginBottom: 8,
    fontSize: 11,
    color: 'gray',
    textAlign: 'right',
  },

  // INLINE INFO MODAL
  inlineInfoOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  inlineInfoCard: {
    backgroundColor: 'white',
    padding: 18,
    borderRadius: 8,
    alignItems: 'center',
    width: '80%',
  },
  inlineInfoText: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 12,
    color: '#333',
  },
  inlineInfoButton: {
    backgroundColor: 'black',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 20,
  },
  inlineInfoButtonText: {
    color: 'white',
    fontSize: 14,
  },

  // CANCEL MODAL
  cancelModal: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cancelModalContent: {
    backgroundColor: 'white',
    borderRadius: 10,
    padding: 20,
    width: '90%',
    alignItems: 'center',
    shadowOpacity: 0.2,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 5,
    elevation: 5,
  },
  cancelModalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#333',
    textAlign: 'center',
  },
  cancelModalSubtitle: {
    fontSize: 14,
    marginBottom: 10,
    color: '#666',
    textAlign: 'center',
  },
  cancelModalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
    marginTop: 12,
  },
  closeButton: {
    position: 'absolute',
    top: 10,
    right: 10,
  },
  cancelModalConfirmText: {
    color: 'black',
    fontSize: 14,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  cancelModalBackButton: {
    backgroundColor: '#FDF05D',
    padding: 10,
    borderRadius: 20,
  },
  cancelModalBackText: {
    color: 'black',
    fontSize: 14,
    textAlign: 'center',
    fontWeight: 'bold',
  },

  // REDEEM STATUS
  redeemedContainer: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  redeemedText: {
    fontSize: 14,
    color: '#000',
    textAlign: 'left',
  },

  // MEDIA CONTENT
  mediaContentSection: {
    marginTop: 16,
    paddingHorizontal: 0,
  },
  mediaItemContainer: {
    marginBottom: 24,
    borderRadius: 20,
    backgroundColor: 'white',
    padding: 16,
    elevation: 2,
    shadowColor: 'black',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 5,
  },
  mediaImage: {
    width: '100%',
    height: 400,
    borderRadius: 20,
    marginBottom: 16,
  },
  mediaContentTextSection: {
    padding: 16,
    borderRadius: 20,
    backgroundColor: '#f3f3f3',
    textAlign: 'center',
  },
  linkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  linkIcon: {
    marginRight: 5,
  },
  mediaLinkText: {
    fontSize: 16,
    color: 'black',
    fontWeight: 'bold',
  },
  mediaMessageText: {
    fontSize: 15,
    color: 'black',
    textAlign: 'center',
  },
  downloadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12,
  },
  downloadText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: 'black',
    marginLeft: 5,
  },

  // MODAL BASE
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    width: '80%',
    padding: 20,
    backgroundColor: 'white',
    borderRadius: 10,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 5,
    elevation: 5,
  },
  modalText: {
    fontSize: 16,
    fontWeight: 'bold',
    marginVertical: 10,
    textAlign: 'center',
    color: '#333',
  },
  modalButton: {
    marginTop: 15,
    backgroundColor: '#FDF05D',
    padding: 10,
    borderRadius: 5,
    width: '80%',
    alignItems: 'center',
  },
  modalButtonText: {
    fontSize: 15,
    fontWeight: 'bold',
    color: 'black',
  },

  // CHAT INLINE PREVIEW
  chatContainer: {
    marginVertical: 10,
    borderRadius: 10,
    backgroundColor: '#fff',
    padding: 10,
  },
  chatHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    borderBottomWidth: 1,
    borderColor: '#ddd',
    paddingBottom: 5,
  },
  chatHeaderText: {
    fontSize: 15,
    fontWeight: 'bold',
    color: 'black',
    marginLeft: 5,
  },
  chatBubble: {
    padding: 8,
    marginVertical: 4,
    borderRadius: 10,
    maxWidth: '75%',
  },
  sentMessage: {
    backgroundColor: '#FDF05D',
    alignSelf: 'flex-end',
    borderTopRightRadius: 0,
  },
  receivedMessage: {
    backgroundColor: '#ffffff',
    alignSelf: 'flex-start',
    borderTopLeftRadius: 0,
  },
  chatUser: {
    fontSize: 10,
    color: '#555',
    marginBottom: 3,
  },
  chatText: {
    fontSize: 13,
    color: '#000',
  },

  // REVEAL OVERLAY
  revealOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
    zIndex: 999,
  },
  revealCard: {
    width: 200,
    height: 235,
    backgroundColor: '#d9d9d9',
    borderRadius: 20,
    elevation: 10,
    shadowColor: '#000',
    shadowOpacity: 0.15,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 8,
  },

  // CHAT MODAL
  chatModalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  chatModalContainer: {
    backgroundColor: '#f3f3f3',
    width: '90%',
    maxWidth: 600,
    height: '80%',
    borderRadius: 15,
    padding: 10,
    justifyContent: 'space-between',
  },
  chatHeaderSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    paddingHorizontal: 15,
    backgroundColor: '#fff',
    borderTopLeftRadius: 15,
    borderTopRightRadius: 15,
  },
  chatHeaderTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'black',
    flex: 1,
    marginLeft: 10,
  },
  chatCloseButton: {
    padding: 5,
  },
  chatMessagesSection: {
    flex: 1,
    padding: 10,
  },
  chatInputSection: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderBottomLeftRadius: 15,
    borderTopLeftRadius: 7,
    borderTopRightRadius: 7,
    borderBottomRightRadius: 15,
  },
  chatTextInput: {
    flex: 1,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#ccc',
    paddingHorizontal: 15,
    paddingVertical: 8,
    marginRight: 10,
    backgroundColor: '#fff',
    fontSize: 14,
    color: 'black',
  },
  sendButton: {
    borderRadius: 20,
    padding: 10,
  },

  // MOVE MODAL
  moveModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  moveModalContent: {
    width: '90%',
    maxWidth: 450,
    maxHeight: 500,
    backgroundColor: 'white',
    borderRadius: 15,
    padding: 20,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 5,
    elevation: 10,
  },
  moveModalClose: {
    position: 'absolute',
    top: 12,
    right: 12,
    padding: 8,
  },
  moveModalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'black',
    marginBottom: 15,
    textAlign: 'center',
  },
  modalPickerWrapper: {
    width: '100%',
    alignItems: 'center',
    overflow: 'hidden',
  },
  moveModalConfirmButton: {
    backgroundColor: 'black',
    borderRadius: 25,
    paddingVertical: 12,
    paddingHorizontal: 30,
    alignItems: 'center',
    marginTop: 15,
    marginBottom: 10,
  },
  moveModalConfirmText: {
    color: 'white',
    fontSize: 15,
    fontWeight: 'bold',
  },

  // QUICK TOAST
  quickToast: {
    position: 'absolute',
    top: 18,
    right: 18,
    backgroundColor: '#f3f3f3',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 20,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 9999,
    shadowColor: '#000',
    shadowOpacity: 0.1,
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 3,
    elevation: 3,
  },
  quickToastText: {
    color: 'black',
    fontWeight: '600',
    fontSize: 13,
  },

  // REWARD MODAL
  modalOverlayReward: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalCardReward: {
    width: 340,
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: 25,
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
    elevation: 6,
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 8,
  },
  tickCircleReward: {
    width: 70,
    height: 70,
    borderRadius: 35,
    backgroundColor: '#00C851',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 15,
  },
  mainTitleReward: {
    fontSize: 23,
    fontWeight: '800',
    textAlign: 'center',
    marginBottom: 8,
    color: 'black',
  },
  subTextReward: {
    fontSize: 16,
    color: '#333',
    textAlign: 'center',
    marginBottom: 15,
  },
  infoCardReward: {
    backgroundColor: '#f3f3f3',
    borderRadius: 12,
    padding: 18,
    marginBottom: 15,
    width: '100%',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 3,
  },
  infoTextReward: {
    fontSize: 15,
    color: '#333',
    textAlign: 'center',
    lineHeight: 22,
  },
  footerTextReward: {
    fontSize: 14,
    color: '#555',
    textAlign: 'center',
    marginBottom: 20,
  },
  buttonReward: {
    backgroundColor: 'black',
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 20,
  },
  buttonTextReward: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 15,
  },
creatorSocialProofWrap: {
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignSelf: 'center',
  maxWidth: '92%',
  marginTop: 2,
  marginBottom: 10,
},
creatorProofBadge: {
  flexDirection: 'row',
  alignItems: 'center',
  backgroundColor: '#f7f7f7',
  borderWidth: 1,
  borderColor: '#e9e9e9',
  borderRadius: 999,
  paddingVertical: 5,
  paddingHorizontal: 9,
  marginHorizontal: 4,
  marginBottom: 6,
},
creatorProofIcon: {
  marginRight: 5,
},
creatorProofText: {
  fontSize: 14,
  color: '#444',
  fontWeight: '600',
  lineHeight: 14,
},
proposalInfoCard: {
  width: '100%',
  backgroundColor: '#eff6ff',
  borderWidth: 2,
  borderColor: '#2563eb',
  borderRadius: 18,
  padding: 16,
  marginBottom: 14,
  shadowColor: '#2563eb',
  shadowOffset: { width: 0, height: 8 },
  shadowOpacity: 0.12,
  shadowRadius: 14,
  elevation: 5,
},

proposalHeaderRow: {
  flexDirection: 'row',
  alignItems: 'flex-start',
  marginBottom: 10,
},

proposalIconCircle: {
  width: 34,
  height: 34,
  borderRadius: 17,
  backgroundColor: '#ffffff',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: 10,
  borderWidth: 1,
  borderColor: '#bfdbfe',
},

proposalBadgeRow: {
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  marginBottom: 6,
},

proposalTextBadge: {
  backgroundColor: '#2563eb',
  borderRadius: 999,
  paddingHorizontal: 10,
  paddingVertical: 4,
  marginRight: 6,
  marginBottom: 4,
},

proposalTextBadgeLabel: {
  color: '#ffffff',
  fontSize: 11,
  fontWeight: '900',
  letterSpacing: 0.4,
  textTransform: 'uppercase',
},

proposalSubBadge: {
  backgroundColor: '#dbeafe',
  borderColor: '#93c5fd',
  borderWidth: 1,
  borderRadius: 999,
  paddingHorizontal: 10,
  paddingVertical: 4,
  marginBottom: 4,
},

proposalSubBadgeLabel: {
  color: '#1e3a8a',
  fontSize: 11,
  fontWeight: '800',
},

proposalInfoTitle: {
  color: '#111827',
  fontSize: 17,
  fontWeight: '600',
  marginTop: 2,
},

proposalInfoBody: {
  color: '#1e3a8a',
  fontSize: 13,
  fontWeight: '500',
  lineHeight: 19,
},
proposalAcceptButton: {
  backgroundColor: '#2563eb',
  paddingVertical: 18,
  paddingHorizontal: 22,
  borderRadius: 18,
},

proposalAcceptButtonLabel: {
  color: '#fff',
  fontWeight: '800',
},
proposalAmendChip: {
  marginTop: 12,
  alignSelf: 'flex-start',
  flexDirection: 'row',
  alignItems: 'center',
  backgroundColor: '#ffffff',
  borderWidth: 1,
  borderColor: '#bfdbfe',
  borderRadius: 999,
  paddingVertical: 8,
  paddingHorizontal: 12,
},

proposalAmendChipText: {
  color: '#2563eb',
  fontSize: 13,
  fontWeight: '700',
},

tutorialBadge: {
  backgroundColor: '#FDF05D',
  color: '#000',
  fontSize: 10,
  fontWeight: '900',
  letterSpacing: 1.2,
  textTransform: 'uppercase',
  paddingVertical: 5,
  paddingHorizontal: 9,
  borderRadius: 999,
  overflow: 'hidden',
  marginLeft: 8,
},
tutorialOverlay: {
  ...StyleSheet.absoluteFillObject,
  zIndex: 9999,
},
tutorialDim: {
  position: 'absolute',
  backgroundColor: 'rgba(0,0,0,0.60)',
},
tutorialOutline: {
  position: 'absolute',
  borderWidth: 3,
  borderColor: '#FDF05D',
  borderRadius: 18,
  shadowColor: '#FDF05D',
  shadowOpacity: 0.32,
  shadowRadius: 18,
  elevation: 20,
},
tutorialTooltip: {
  position: 'absolute',
  left: 18,
  right: 18,
  bottom: 22,
  alignSelf: 'center',
  maxWidth: 520,
  backgroundColor: '#ffffff',
  borderRadius: 22,
  padding: 18,
  borderWidth: 1,
  borderColor: 'rgba(17,24,39,0.10)',
  shadowColor: '#000',
  shadowOffset: { width: 0, height: 14 },
  shadowOpacity: 0.22,
  shadowRadius: 30,
  elevation: 30,
},
tutorialStepPill: {
  alignSelf: 'flex-start',
  backgroundColor: '#111827',
  borderRadius: 999,
  paddingVertical: 5,
  paddingHorizontal: 9,
  marginBottom: 10,
},
tutorialStepPillText: {
  color: '#FDF05D',
  fontSize: 11,
  fontWeight: '900',
  letterSpacing: 0.6,
},
tutorialTitle: {
  fontSize: 20,
  lineHeight: 25,
  fontWeight: '900',
  color: '#111827',
  marginBottom: 7,
},
tutorialBody: {
  fontSize: 13,
  lineHeight: 20,
  color: '#4b5563',
  fontWeight: '600',
},
tutorialNextButton: {
  marginTop: 14,
  backgroundColor: '#FDF05D',
  borderRadius: 999,
  paddingVertical: 12,
  paddingHorizontal: 16,
  alignItems: 'center',
},
tutorialNextText: {
  color: '#111827',
  fontWeight: '900',
  fontSize: 14,
},
tutorialFullScreenDim: {
  ...StyleSheet.absoluteFillObject,
  backgroundColor: 'rgba(0,0,0,0.66)',
},
tutorialCenterCard: {
  position: 'absolute',
  left: 18,
  right: 18,
  top: '25%',
  alignSelf: 'center',
  maxWidth: 540,
  backgroundColor: '#ffffff',
  borderRadius: 26,
  padding: 22,
  borderWidth: 1,
  borderColor: 'rgba(17,24,39,0.10)',
  shadowColor: '#000',
  shadowOffset: { width: 0, height: 18 },
  shadowOpacity: 0.28,
  shadowRadius: 36,
  elevation: 40,
},
tutorialWelcomeIcon: {
  width: 52,
  height: 52,
  borderRadius: 26,
  backgroundColor: '#FDF05D',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 14,
},
tutorialProgress: {
  marginTop: 12,
  color: '#9ca3af',
  fontSize: 11,
  fontWeight: '700',
},
tutorialAcceptFlash: {
  ...StyleSheet.absoluteFillObject,
  zIndex: 12000,
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(22,163,74,0.90)',
},
tutorialAcceptCircle: {
  width: 92,
  height: 92,
  borderRadius: 46,
  backgroundColor: '#ffffff',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 14,
},
tutorialAcceptTitle: {
  color: '#ffffff',
  fontSize: 26,
  fontWeight: '900',
},
tutorialAcceptSub: {
  color: '#dcfce7',
  fontSize: 13,
  fontWeight: '700',
  marginTop: 5,
},
tutorialEmailBadge: {
  alignSelf: 'flex-start',
  backgroundColor: '#fee2e2',
  borderWidth: 1,
  borderColor: '#fecaca',
  borderRadius: 999,
  paddingVertical: 5,
  paddingHorizontal: 9,
  marginBottom: 10,
},
tutorialEmailBadgeText: {
  color: '#991b1b',
  fontSize: 10,
  fontWeight: '900',
  letterSpacing: 0.7,
  textTransform: 'uppercase',
},
tutorialEmailBox: {
  marginTop: 12,
  padding: 12,
  borderRadius: 14,
  backgroundColor: '#f8fafc',
  borderWidth: 1,
  borderColor: '#e5e7eb',
},
tutorialEmailLabel: {
  color: '#6b7280',
  fontSize: 11,
  fontWeight: '800',
  marginBottom: 4,
},
tutorialEmailText: {
  color: '#111827',
  fontSize: 14,
  fontWeight: '900',
},
tutorialChoiceRow: {
  flexDirection: 'row',
  gap: 8,
  marginTop: 12,
},
tutorialChoice: {
  flex: 1,
  borderRadius: 14,
  borderWidth: 1,
  borderColor: '#d1d5db',
  backgroundColor: '#ffffff',
  paddingVertical: 11,
  paddingHorizontal: 10,
  alignItems: 'center',
},
tutorialChoiceSelected: {
  borderColor: '#16a34a',
  backgroundColor: '#f0fdf4',
},
tutorialChoiceText: {
  color: '#374151',
  fontSize: 12,
  fontWeight: '800',
  textAlign: 'center',
},
tutorialChoiceTextSelected: {
  color: '#166534',
},
tutorialHelpBox: {
  marginTop: 10,
  padding: 11,
  borderRadius: 13,
  backgroundColor: '#fff7ed',
  borderWidth: 1,
  borderColor: '#fed7aa',
},
tutorialHelpText: {
  color: '#9a3412',
  fontSize: 12,
  lineHeight: 18,
  fontWeight: '700',
},
tutorialTestButton: {
  marginTop: 12,
  backgroundColor: '#111827',
  borderRadius: 999,
  paddingVertical: 12,
  paddingHorizontal: 15,
  alignItems: 'center',
},
tutorialTestButtonText: {
  color: '#ffffff',
  fontSize: 13,
  fontWeight: '900',
},
tutorialSuccessText: {
  marginTop: 9,
  color: '#166534',
  fontSize: 12,
  lineHeight: 18,
  fontWeight: '800',
},
tutorialErrorText: {
  marginTop: 9,
  color: '#991b1b',
  fontSize: 12,
  lineHeight: 18,
  fontWeight: '800',
},
analyticsOverlay: {
  flex: 1,
  backgroundColor: 'rgba(0,0,0,0.55)',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 18,
},
analyticsCard: {
  width: '100%',
  maxWidth: 450,
  backgroundColor: '#ffffff',
  borderRadius: 22,
  padding: 20,
},
analyticsTitle: {
  fontSize: 19,
  fontWeight: '900',
  color: '#111827',
  marginBottom: 12,
},
analyticsMetricRow: {
  flexDirection: 'row',
  justifyContent: 'space-between',
  paddingVertical: 10,
  borderBottomWidth: 1,
  borderBottomColor: '#f1f5f9',
},
analyticsMetricLabel: {
  fontSize: 12,
  color: '#6b7280',
  fontWeight: '700',
},
analyticsMetricValue: {
  fontSize: 13,
  color: '#111827',
  fontWeight: '900',
},
mobileSideNav: {
  width: 74,
},
mobileSideNavInner: {
  paddingHorizontal: 10,
},
mobileContentContainer: {
  paddingHorizontal: 12,
  paddingVertical: 14,
},
mobileTopRow: {
  flexDirection: 'column',
},
mobileProfileColumn: {
  width: '100%',
  marginRight: 0,
},
mobileStatusColumn: {
  width: '100%',
  marginTop: 14,
},
});


export default BookingTutorial;
